/**
 * 书签数据模型
 * 定义书签的数据结构和验证规则
 */

// 书签类别定义
export const BOOKMARK_CATEGORIES = {
  TOOLS: 'tools',
  SOCIAL: 'social',
  LEARNING: 'learning',
  VIDEO: 'video',
  SHOPPING: 'shopping',
  NEWS: 'news',
  ENTERTAINMENT: 'entertainment',
  WORK: 'work',
  OTHER: 'other'
};

// 类别显示名称
export const CATEGORY_LABELS = {
  [BOOKMARK_CATEGORIES.TOOLS]: '工具',
  [BOOKMARK_CATEGORIES.SOCIAL]: '社交',
  [BOOKMARK_CATEGORIES.LEARNING]: '学习',
  [BOOKMARK_CATEGORIES.VIDEO]: '视频',
  [BOOKMARK_CATEGORIES.SHOPPING]: '购物',
  [BOOKMARK_CATEGORIES.NEWS]: '新闻',
  [BOOKMARK_CATEGORIES.ENTERTAINMENT]: '娱乐',
  [BOOKMARK_CATEGORIES.WORK]: '工作',
  [BOOKMARK_CATEGORIES.OTHER]: '其他'
};

/**
 * 书签数据结构
 */
export const BookmarkSchema = {
  _id: null,                    // MongoDB ObjectId
  title: '',                    // 书签标题（必填）
  url: '',                      // 书签URL（必填）
  description: '',              // 书签描述（可选）
  category: '',                 // 书签分类（必填）
  tags: [],                     // 标签数组（可选）
  favicon: '',                  // 网站图标URL（可选）
  isPrivate: false,             // 是否私有（可选）
  userId: '',                   // 用户ID（必填）
  createdAt: null,              // 创建时间
  updatedAt: null,              // 更新时间
  clickCount: 0,                // 点击次数
  lastVisited: null             // 最后访问时间
};

/**
 * 验证书签数据
 * @param {Object} bookmark - 书签对象
 * @returns {Object} 验证结果 { isValid: boolean, errors: string[] }
 */
export const validateBookmark = (bookmark) => {
  const errors = [];

  // 验证标题
  if (!bookmark.title || bookmark.title.trim().length === 0) {
    errors.push('标题不能为空');
  } else if (bookmark.title.length > 100) {
    errors.push('标题长度不能超过100个字符');
  }

  // 验证URL
  if (!bookmark.url || bookmark.url.trim().length === 0) {
    errors.push('网址不能为空');
  } else {
    try {
      new URL(bookmark.url);
    } catch {
      errors.push('请输入有效的网址');
    }
  }

  // 验证分类
  if (!bookmark.category || bookmark.category.trim().length === 0) {
    errors.push('分类不能为空');
  } else if (!Object.values(BOOKMARK_CATEGORIES).includes(bookmark.category)) {
    errors.push('无效的分类');
  }

  // 验证描述长度
  if (bookmark.description && bookmark.description.length > 500) {
    errors.push('描述长度不能超过500个字符');
  }

  // 验证标签
  if (bookmark.tags && Array.isArray(bookmark.tags)) {
    if (bookmark.tags.length > 10) {
      errors.push('标签数量不能超过10个');
    }
    for (const tag of bookmark.tags) {
      if (typeof tag !== 'string' || tag.length > 20) {
        errors.push('每个标签长度不能超过20个字符');
        break;
      }
    }
  }

  // 验证用户ID
  if (!bookmark.userId || bookmark.userId.trim().length === 0) {
    errors.push('用户ID不能为空');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * 创建新书签对象
 * @param {Object} data - 书签数据
 * @param {string} userId - 用户ID
 * @returns {Object} 新书签对象
 */
export const createBookmark = (data, userId) => {
  const now = new Date();
  
  return {
    title: data.title?.trim() || '',
    url: data.url?.trim() || '',
    description: data.description?.trim() || '',
    category: data.category || BOOKMARK_CATEGORIES.OTHER,
    tags: Array.isArray(data.tags) ? data.tags.map(tag => tag.trim()).filter(Boolean) : [],
    favicon: data.favicon?.trim() || '',
    isPrivate: Boolean(data.isPrivate),
    userId: userId,
    createdAt: now,
    updatedAt: now,
    clickCount: 0,
    lastVisited: null
  };
};

/**
 * 更新书签对象
 * @param {Object} existingBookmark - 现有书签
 * @param {Object} updateData - 更新数据
 * @returns {Object} 更新后的书签对象
 */
export const updateBookmark = (existingBookmark, updateData) => {
  const updatedBookmark = {
    ...existingBookmark,
    ...updateData,
    updatedAt: new Date()
  };

  // 确保某些字段的数据类型正确
  if (updateData.tags) {
    updatedBookmark.tags = Array.isArray(updateData.tags) 
      ? updateData.tags.map(tag => tag.trim()).filter(Boolean) 
      : [];
  }

  if (updateData.isPrivate !== undefined) {
    updatedBookmark.isPrivate = Boolean(updateData.isPrivate);
  }

  return updatedBookmark;
};

/**
 * 增加书签点击次数
 * @param {Object} bookmark - 书签对象
 * @returns {Object} 更新后的书签对象
 */
export const incrementClickCount = (bookmark) => {
  return {
    ...bookmark,
    clickCount: (bookmark.clickCount || 0) + 1,
    lastVisited: new Date(),
    updatedAt: new Date()
  };
};

export default {
  BOOKMARK_CATEGORIES,
  CATEGORY_LABELS,
  BookmarkSchema,
  validateBookmark,
  createBookmark,
  updateBookmark,
  incrementClickCount
}; 